import Objdict from "objdict"
import shortid from "shortid"

import Stage from "@frog/source/models/Stage.js"
import Step from "@frog/source/models/Step.js"
import World from "@frog/source/models/World.js"
import Zone from "@frog/source/models/Zone.js"
import Geometry from "@frog/source/utility/Geometry.js"
import Elements from "@frog/source/data/elements.js"
import Tiled from "@frog/source/utility/integration/Tiled.js"
import * as Motions from "@frog/source/juice/utility/Motions.js"
import InkDialogue from "@frog/source/utility/integration/InkDialogue.js"

import { Dialogue } from "@frog/source/data/plotscripts.js"
import App from "@frog/source/models/App.js"
import Timings from "@frog/source/juice/utility/Timings.js"

function canTargetAlly({performerKey, stage, position}) {
    const performer = stage.units[performerKey]
    const target = Stage.findUnitByPosition(stage, position)
    if(target == undefined) return false
    if(target.affiliation != performer.affiliation) return false
    return true
}

const skills = {}

skills["inflict-damage"] = {
    "key": "inflict-damage",
    "name": "Attack",
    "role": "damage",

    "type": "invoke",
    "range": null, // will default to the player's attackrange.

    "canCounterattack": true,
    "canBeCounterattacked": true,
    "isRenderedEvenIfCanNotTarget": true,

    "power": 0, // will default to the player's power.
    "isIgnoringArmor": false,
    "isIgnoringElements": false,

    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation == "none") return false
        if(performer.canBetray != true && target.affiliation == performer.affiliation) return false
        return true
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const distance = Geometry.distance(position, performer.position)

        Step.add(step, {"mark": 0, "flourish": {"name": "SwingBackMovement", "unit": {"key": performer.key}, "direction": direction, "position": performer.position}})
        Step.add(step, {"mark": 180, "audio": {"triggerKey": "Unit:Attack:Melee"}})

        const target = Stage.findUnitByPosition(step.omegastage, position)

        if(target == undefined) {
            return step
        }

        /////////////
        // DAMAGE //
        ///////////

        const range = (this.range && this.range.max) || performer.attackrange || 1
        let hitmark = 200 + ((range - 1) * 50)

        let innatePower = performer.power || 0
        if(performer.power == 0) innatePower = 0

        const totalDamage = innatePower
        let damage = totalDamage

        if(damage == 0) {
            return
        }

        if(performer.attacktype == "steel") {
            damage -= target.defense || 0
        }
        if(performer.attacktype == "magic") {
            damage -= target.resistance || 0
        }

        const attackElement = performer.element || performer.attackelement
        let isSuperEffective = false
        let isIneffective = false
        if(attackElement != undefined) {
            if(Elements[target.element]?.weaknesses != undefined
            && Elements[target.element].weaknesses.includes(attackElement)) {
                // damage += Math.ceil(damage * (1 / 2))
                isSuperEffective = true
            }
            if(Elements[target.element]?.strengths != undefined
            && Elements[target.element].strengths.includes(attackElement)) {
                damage -= Math.ceil(damage * (1 / 4))
                isIneffective = true
            }
        }

        // PERFORMING ANIMATION HERE //

        if(damage == 0) {
            Step.add(step, {
                "mark": hitmark,
                "audio": {"triggerKey": "Unit:Attack:NoDamage"}
            })
        }
        if(damage > 0) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": target.key,
                    "health": Math.min(target.maxhealth, Math.max(0, target.health - damage)),
                },
            })

            Step.add(step, {
                "mark": hitmark,
                "flourish": {
                    "name": "NudgedMovement",
                    "unit": {"key": target.key},
                    "position": target.position,
                    "direction": direction,
                }
            })
            Step.add(step, {
                "mark": hitmark,
                "audio": {"triggerKey": target.health < 0 ? "Unit:Attack:FinalHit" : "Unit:Attack:Hit"}
            })
        }
        Step.add(step, {
            "mark": hitmark,
            "flourish": {
                "name": "TextFloat",
                "type": "damage",
                "unitKey": target.key,
                "position": target.position,
                "text": (damage > 0 ? "-" : "") + damage,
            }
        })

        if(isSuperEffective) {
            damage += Math.ceil(damage * (1 / 2))
            let mark = hitmark + 133

            Step.add(step, {
                "mark": mark + 0,
                "flourish": {
                    "name": "SwingBackMovement",
                    "unit": {"key": performer.key},
                    "direction": direction,
                    "position": performer.position
                }
            })
            Step.add(step, {
                "mark": mark + 180,
                "audio": {"triggerKey": "Unit:Attack:Melee"}
            })

            if(damage == 0) {
                Step.add(step, {
                    "mark": mark + hitmark,
                    "audio": {"triggerKey": "Unit:Attack:NoDamage"}
                })
            }
            if(damage > 0) {
                Step.add(step, {
                    "mark": mark + hitmark,
                    "unit": {
                        "key": target.key,
                        "health": Math.min(target.maxhealth, Math.max(0, target.health - damage)),
                    },
                })

                Step.add(step, {
                    "mark": mark + hitmark,
                    "flourish": {
                        "name": "NudgedMovement",
                        "unit": {"key": target.key},
                        "position": target.position,
                        "direction": direction,
                    }
                })
                Step.add(step, {
                    "mark": mark + hitmark,
                    "audio": {"triggerKey": target.health < 0 ? "Unit:Attack:FinalHit" : "Unit:Attack:Hit"}
                })
            }
            Step.add(step, {
                "mark": mark + hitmark,
                "flourish": {
                    "name": "TextFloat",
                    "type": "damage",
                    "unitKey": target.key,
                    "position": target.position,
                    "text": (damage > 0 ? "-" : "") + damage,
                }
            })
        }

        const encounter = Object.values(stage.world.zones).find((zone) => {
            return Zone.isInBounds(zone, target.position)
        })

        Object.values(stage.units).forEach((unit) => {
            if(unit.affiliation == "bad"
            && Zone.isInBounds(encounter, unit.position)) {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": unit.key,
                        "state": "aggroed",
                    }
                })
            }
        })

        if(performer.affiliation == "good") {
            if(App.tutorial.hasAttacked != true) {
                Step.add(step, {"mark": hitmark, "func": function() {
                    App.tutorial.hasAttacked = true
                }})
            }
            if(isIneffective == true
            && App.tutorial.hasAttackedIneffectively != true) {
                Step.add(step, {"mark": hitmark, "func": function() {
                    App.tutorial.hasAttackedIneffectively = true
                }})
            }
            if(performer.element == "earth"
            && target.element == "water"
            && App.tutorial.hasAttackedEffectivelyWithEarth != true) {
                Step.add(step, {"mark": hitmark, "func": function() {
                    App.tutorial.hasAttackedEffectivelyWithEarth = true
                }})
            }
        }

        step.followupSteps = []

        if(performer.canBetray == true) {
            if(performer.affiliation == "good"
            && target.affiliation == "good") {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": target.key,
                        "affiliation": "bad",
                        "bodyKey": "bad",
                    }
                })
                step.followupSteps.push(Stage.processEventSteps(step.omegastage, InkDialogue(target.key + "_" + "betrayed", Stage.copy(step.omegastage))))
            }
        }

        return step
    }
}

skills["restore-health"] = {
    "key": "restore-health",
    "name": "Heal",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "power": 0,

    "canTarget": canTargetAlly,

    "calculateStep": function({performerKey, effectKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]

        const direction = Geometry.direction(position, performer.position)

        const deltastages = []
        const HIT_MARK = 0

        Step.add(step, {"mark": HIT_MARK, "flourish": {"name": "NudgedMovement", "unit": {"key": performer.key}, "direction": direction, "position": performer.position}})
        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Heal"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        let healing = this.power + (performer.power || 0)
        if(target.health + healing > target.maxhealth) {
            healing = target.maxhealth - target.health
        }

        Step.add(step, {
            "mark": HIT_MARK,
            "unit": {
                "key": target.key,
                "health": target.health + healing
            },
        })

        if(target.effects.burning != undefined) {
            Step.add(step, {
                "mark": HIT_MARK,
                "unit": {
                    "key": target.key,
                    "effects": {
                        "burning": undefined,
                    }
                },
            })
        }

        Step.add(step, {"mark": HIT_MARK, "flourish": {"name": "NudgedMovement", "unit": {"key": target.key}, "direction": Geometry.flip(direction), "position": target.position}})
        Step.add(step, {"mark": HIT_MARK, "flourish": {"name": "TextFloat", "position": target.position, "text": healing}})

        return step
    }
}

skills["recruit"] = {
    "key": "recruit",
    "name": "Recruit",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "isHidden": true,

    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation != "none") return false
        return true
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const distance = Geometry.distance(position, performer.position)

        Step.add(step, {"mark": 0, "flourish": {"name": "SwingBackMovement", "unit": {"key": performer.key}, "direction": direction, "position": performer.position}})
        Step.add(step, {"mark": 180, "audio": {"triggerKey": "Unit:Attack:Melee"}})

        const target = Stage.findUnitByPosition(step.omegastage, position)

        if(target == undefined
        || target.affiliation != "none") {
            return step
        }

        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "affiliation": performer.affiliation,
            },
        })
        step.followupSteps = []
        step.followupSteps.push(Stage.processEventSteps(step.omegastage, Dialogue({
            "type": "CharacterSplashBox",
            "data": {"unitKey": target.key}
        })))

        if(performer.affiliation == "good") {
            if(App.tutorial.hasRecruitedShield != true) {
                if(target.key == "spearknight") {
                    Step.add(step, {"mark": 200, "func": function() {
                        App.tutorial.hasRecruitedShield = true
                    }})
                }
            }
        }

        return step
    }
}

skills["rally"] = {
    "key": "rally",
    "name": "Rally",
    "role": "support",

    "type": "invoke",
    "range": 1,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation != performer.affiliation) return false
        if(target.energy["invoke skill"] == true && target.energy["move"] == true) return false
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, {
            "mark": 0,
            "flourish": {
                "name": "SwingBackMovement",
                "unit": {"key": performer.key},
                "position": performer.position,
                "direction": direction,
            }
        })

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        Step.add(step, {
            "mark": 200,
            "flourish": {
                "name": "NudgedMovement",
                "unit": {"key": target.key},
                "position": target.position,
                "direction": direction,
            },
            "unit": {
                "key": target.key,
                "energy": {"move": true, "invoke skill": true},
            }
        })

        return step
    }
}

skills["push"] = {
    "key": "push",
    "name": "Push",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "canTarget": canTargetAlly,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const vector = Geometry.vector(position, performer.position)

        Step.add(step, {
            "mark": 0,
            "flourish": {
                "name": "SwingBackMovement",
                "unit": {"key": performer.key},
                "position": performer.position,
                "direction": direction,
            }
        })

        const previousPosition = position
        const nextPosition = Geometry.add(position, vector)

        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousPosition, nextPosition]
            },
        })

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            Step.add(step, {
                "mark": 200,
                "flourish": {
                    "name": "PushedMovement",
                    "unit": {"key": target.key},
                    "startPosition": previousPosition,
                    "endPosition": nextPosition,
                },
                "unit": {
                    "key": target.key,
                    "position": nextPosition,
                }
            })
        } else if(collision != undefined) {
            Step.add(step, {
                "mark": 200,
                "flourish": {
                    "name": "NudgedMovement",
                    "unit": {"key": target.key},
                    "position": target.position,
                    "direction": direction,
                },
            })

            const damage = 1
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "health": target.health - damage
                },
            })

            if(collision.unit != undefined) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": collision.unit.key,
                        "health": collision.unit.health - damage
                    },
                })
            }
        }

        return step
    }
}

skills["swap"] = {
    "key": "swap",
    "name": "Swap",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "canTarget": canTargetAlly,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) {
            Step.add(step, [
                {
                    "mark": 0,
                    "flourish": {
                        "name": "SwingBackMovement",
                        "unit": {"key": performer.key},
                        "position": performer.position,
                        "direction": direction,
                    }
                },
                {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}}
            ])
            return step
        }

        Step.add(step, [
            {
                "mark": 0,
                "flourish": {
                    "name": "PushedMovement",
                    "unit": {"key": performer.key},
                    "startPosition": performer.position,
                    "endPosition": target.position,
                },
                "unit": {
                    "key": performer.key,
                    "position": target.position,
                }
            },
            {
                "mark": 0,
                "flourish": {
                    "name": "PushedMovement",
                    "unit": {"key": target.key},
                    "startPosition": target.position,
                    "endPosition": performer.position,
                },
                "unit": {
                    "key": target.key,
                    "position": performer.position,
                }
            },
            {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}}
        ])

        return step
    }
}

skills["pull"] = {
    "key": "pull",
    "name": "Pullback",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "canTarget": canTargetAlly,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const oppositeDirection = Geometry.flip(direction)

        if(direction.x != 0
        && direction.y != 0) {
            console.log("Cannot calculate pull skill with non-orthogonal direction.")
            return
        }

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) {
            return step
        }

        const previousPosition = performer.position
        const nextPosition = Geometry.add(performer.position, oppositeDirection)

        const previousTargetPosition = target.position
        const nextTargetPosition = Geometry.add(target.position, oppositeDirection)

        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousPosition, nextPosition]
            },
        })
        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousTargetPosition, nextTargetPosition]
            },
        })

        const collision = Stage.findCollision({
            "stage": stage,
            previousPosition,
            nextPosition,
        })

        if(collision == undefined) {
            Step.add(step, {
                "mark": 0,
                "flourish": {
                    "name": "PushedMovement",
                    "unit": {"key": performer.key},
                    "startPosition": previousPosition,
                    "endPosition": nextPosition,
                },
                "unit": {
                    "key": performer.key,
                    "position": nextPosition,
                },
            })
            Step.add(step, {
                "mark": 0,
                "flourish": {
                    "name": "PushedMovement",
                    "unit": {"key": target.key},
                    "startPosition": previousTargetPosition,
                    "endPosition": nextTargetPosition,
                },
                "unit": {
                    "key": target.key,
                    "position": nextTargetPosition,
                }
            })
            Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        } else if(collision != undefined) {
            Step.add(step, {
                "mark": 0,
                "flourish": {
                    "name": "NudgedMovement",
                    "unit": {"key": performer.key},
                    "position": performer.position,
                    "direction": direction,
                }
            })
            Step.add(step, {
                "mark": 0,
                "flourish": {
                    "name": "NudgedMovement",
                    "unit": {"key": target.key},
                    "position": target.position,
                    "direction": direction,
                }
            })
            Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        }

        return step
    }
}

skills["suplex"] = {
    "key": "suplex",
    "name": "Suplex",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "canTarget": canTargetAlly,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({"type": "invoke skill", "alphastage": stage})

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, {
            "mark": 0,
            "flourish": {
                "name": "SwingBackMovement",
                "unit": {"key": performer.key},
                "position": performer.position,
                "direction": direction,
            }
        })
        Step.add(step, {
            "mark": 0,
            "flourish": {
                "name": "Audio",
                "audio": {"triggerKey": "Unit:Attack:Utility"}
            }
        })

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const previousPosition = performer.position
        const nextPosition = Geometry.add(performer.position, Geometry.flip(direction))

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            Step.add(step, {
                "mark": 200,
                "flourish": {
                    "name": "PushedMovement",
                    "unit": {"key": target.key},
                    "startPosition": target.position,
                    "endPosition": nextPosition,
                },
                "unit": {
                    "key": target.key,
                    "position": nextPosition,
                },
                "intent": {
                    "type": "reposition",
                    "positions": [target.position, performer.position, nextPosition]
                },
            })
        } else {
            Step.add(step, {
                "mark": 200,
                "flourish": {
                    "name": "NudgedMovement",
                    "unit": {"key": target.key},
                    "position": target.position,
                    "direction": direction,
                },
            })
        }

        return step
    }
}

skills["pivot"] = {
    "key": "pivot",
    "name": "Pivot",
    "role": "support",

    "type": "invoke",
    "range": 1,
    "canTarget": canTargetAlly,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, {
            "mark": 0,
            "flourish": {
                "name": "SwingBackMovement",
                "unit": {"key": performer.key},
                "position": performer.position,
                "direction": direction,
            }
        })
        Step.add(step, {
            "mark": 0,
            "flourish": {
                "name": "Audio",
                "audio": {"triggerKey": "Unit:Attack:Utility"}
            }
        })

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const previousPosition = performer.position
        const nextPosition = Geometry.add(target.position, direction)

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            Step.add(step, {
                "mark": 200,
                "flourish": {
                    "name": "PushedMovement",
                    "unit": {"key": performer.key},
                    "startPosition": performer.position,
                    "endPosition": nextPosition,
                },
                "unit": {
                    "key": performer.key,
                    "position": nextPosition,
                },
                "intent": {
                    "type": "reposition",
                    "positions": [performer.position, target.position, nextPosition]
                },
            })
        } else {
            Step.add(step, {
                "mark": 200,
                "flourish": {
                    "name": "NudgedMovement",
                    "unit": {"key": performer.key},
                    "position": performer.position,
                    "direction": direction,
                },
            })
        }

        return step
    }
}

skills["smite"] = {
    "key": "smite",
    "name": "Push",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "canTarget": canTargetAlly,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, {
            "mark": 0,
            "flourish": {
                "name": "SwingBackMovement",
                "unit": {"key": performer.key},
                "position": performer.position,
                "direction": direction,
            }
        })

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const intendedDistance = 2
        const ray = Stage.findCollisionInDirection({
            "stage": stage,
            "position": position,
            "direction": direction,
            "maxdistance": intendedDistance,
        })

        Step.add(step, {
            "mark": 0,
            "intent": {
                "type": "reposition",
                "positions": ray.castedPositions
            }
        })

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        if(ray.distance > 0) {
            Step.add(step, {
                "mark": 200,
                "flourish": {
                    "name": "PushedMovement",
                    "unit": {"key": target.key},
                    "startPosition": ray.startPosition,
                    "endPosition": ray.endPosition,
                },
                "unit": {
                    "key": target.key,
                    "position": ray.endPosition,
                },
            })
        } else {
            Step.add(step, {
                "mark": 200,
                "flourish": {
                    "name": "NudgedMovement",
                    "unit": {"key": target.key},
                    "position": target.position,
                    "direction": direction,
                },
            })
        }

        if(ray.distance < intendedDistance) {
            const damage = intendedDistance - ray.distance
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "health": target.health - damage
                },
            })

            if(ray?.collision?.unit != undefined) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": ray.collision.unit.key,
                        "health": ray.collision.unit.health - damage
                    },
                })
            }
        }

        return step
    }
}

skills.get = function(skillKey) {
    return skills[skillKey]
}

export default skills
